import { IdentifierKeyMap, TIdentifierObject, TReadoutTestStatus, TTestWaterfallStatus } from "@/constants/Test";
import { DimensionKeyMap, TDimensionKey, TDimensionValue } from "@/modules/FDDD/constants";
import type { TUvColorTableResponse } from "@/modules/UVVisualizer/types";
import { TUploadedFile } from "@type/common";
import { ICreateOperatorRequest, IOperator, IUser } from "@type/user";

// Re-export so existing consumers that import TReadoutTestStatus from
// @type/axios keep working without churn. Source of truth: @/constants/Test.
export type { TReadoutTestStatus } from "@/constants/Test";
// General
export interface IServerResponse<T = any> {
  version?: number;
  timestamp?: number;
  code?: string;
  message?: string;
  data: T;
  isSuccess: boolean;
}
export type TFO = "failure" | "observation";
export type TUserInfoResponse = IServerResponse<IUser>;
export type TRouterConfigResponse = IServerResponse<{ key: string; canWrite: boolean }[]>;
export interface TKeyName {
  key: string;
  name: string;
}

// Identifier
export type TIdentifierFilter = "all" | "testSpec" | "noTestSpec" | "withTestWaterfall" | "noTestWaterfall" | "withTestData" | "noFamilySpec";
export type TIdentifierParams = Partial<Record<(typeof IdentifierKeyMap)[keyof typeof IdentifierKeyMap], string>> & { filter?: TIdentifierFilter };
export type TIdentifier = TKeyName & {
  children?: TIdentifier[]; // Recursive to support N-level nesting
};
export type TIdentifierResponse = IServerResponse<TIdentifier[]>;
export type TTestDurationListResponse = IServerResponse<TTestDuration[]>;

export interface TShareableInfo {
  identifier: Record<string, string>;
  filters?: Record<string, string>;
  activeTab?: string;
  drawer?: (Record<string, any> & { key: string })[];
  forms?: (Record<string, any> & { key: string })[];
}
export type TShareableLinkResponse = IServerResponse<TShareableInfo>;

/** Request payload for POST /api/create_shareable_link */
export interface ICreateShareableLinkRequest {
  requestParameter: string;
  description: string;
  feature: string;
}

/** Response from POST /api/create_shareable_link */
export type TCreateShareableLinkResponse = IServerResponse<{ uuid: string }>;

/** Data returned by GET /api/get_shareable_link?uuid=<uuid> */
export interface TGetShareableLinkData {
  createdBy: string;
  createdTs: string;
  description: string;
  feature: string;
  requestParameter: string; // JSON string — parse to get the actual payload
  uuid: string;
}

/** Response from GET /api/get_shareable_link */
export type TGetShareableLinkResponse = IServerResponse<TGetShareableLinkData>;

export interface TUserList {
  email: string;
  username: string;
  login: string;
}
export type TUserListResponse = IServerResponse<TUserList[]>;

// Test Spec
export type TSpecType = "derivedTest" | "monitorOnly";
export interface ITestSpecBase {
  testCategory: string;
  testName: string;
  // Spec version string from spec_control.version: FS_<subcategory>_<NN> (family-inherited),
  // BS_<projectId>_<testLocationId>_<NN> (own build spec), or DRAFT_<timestamp>. Surfaced under
  // the Status column (Asana 1211931779388512). Optional — arrives on get_test_spec_list.
  versionId?: string;
  status: "inWork" | "pendingApproval" | "approved";
  subStatus?: "submitApproval" | "notComplete" | "needsRevision" | "managerApproved" | "autoApproved";
  nextStep?: string[];
  testDescription: string;
  specType: TSpecType[];
  derivedFrom?: {
    key: string;
    name: string;
    description?: string;
  };
  approvers?: string[];
  isCommittee?: boolean;
}
export type ITestSpec = ITestSpecBase & TIdentifierObject;

export type TTestSpecListResponse = IServerResponse<{ testList: ITestSpec[] } & Pick<ITestSpecBase, "status" | "nextStep" | "specType">>;
export type TTestSpecDetail = ITestSpec & {
  passCriteria?: string;
  profiles?: TUploadedFile[];
  justifications?: TUploadedFile[];
};
export type TTestSpecDetailResponse = IServerResponse<TTestSpecDetail>;

// ── Test Settings panel (Test Spec detail drawer) ────────────────────
// GET /get_test_spec_sequences — grouping-sequence names for the picker.
export type TTestSpecSequencesResponse = IServerResponse<string[]>;

// GET /get_test_spec_readout_mappings — one row per real readout of a sequence:
// its key-value label pairs plus mapped checks and measurements. Reuses the
// readout-input shapes; only id/key/name/checkCategory (checks) and
// id/component/name/unit (measurements) are populated here. All members are
// optional on the wire (no @required in the Smithy model) — consumers must
// handle null/undefined defensively despite the required-typed composed fields.
export interface IReadoutMappingItem {
  id: number;
  keyValuePair: IReadoutKeyValuePair[];
  checks: IReadoutCheck[];
  measurements: IReadoutMeasurement[];
}
export type TTestSpecReadoutMappingsResponse = IServerResponse<IReadoutMappingItem[]>;
/** POST /submit_test_spec_for_sign_off — build-level approval submission. */
export interface TSubmitTestSpecSignOffRequest {
  projectId: string;
  testLocationId: string;
  phaseId: string;
  buildId: string;
  approvers: string[];
  isCommittee: boolean;
}

export type TCreateTestSpecMode = "createFromScratch" | "copyFromFamily" | "copyFromBuild";

export type TCreateTestSpecRequest = TIdentifierParams &
  (
    | {
        mode: TCreateTestSpecMode[1];
        addFromScratchParams: Partial<TTestSpecDetail>;
      }
    | {
        mode: TCreateTestSpecMode[2];
        copyFromFamilyParams: TIdentifierObject & { testList: string[]; copyTestSettings: boolean };
      }
    | {
        mode: TCreateTestSpecMode[3];
        copyFromBuildParams: TIdentifierObject & { testList: string[]; copyTestSettings: boolean };
      }
  );

// ─── Family Spec ────────────────────────────────────────────────────────────
// Aligned to DT-RadarPlus-Backend-Model (CR-286038137). Status reuses TestSpec's
// vocabulary: inWork | pendingApproval | approved (+ subStatus). A rejected draft
// is inWork + subStatus "needsRevision" (BE has no distinct rejected status and no
// reject reason/reviewer/date). Specs are keyed by testId; versions by versionId.

/**
 * Summary row from GET /get_family_spec_list. Shows the APPROVED version as primary
 * when one exists; `draftVersionId` is populated only when a separate in-progress
 * draft (pending or rejected) also exists — it drives the row's ▲ marker.
 */
export interface IFamilySpecSummary {
  testId: string;
  testName: string;
  testCategory: string;
  status: ITestSpecBase["status"]; // "inWork" | "pendingApproval" | "approved"
  subStatus?: ITestSpecBase["subStatus"];
  testDescription: string;
  versionId: string;
  /** DRAFT_ version string; present only when an approved row AND a draft coexist. */
  draftVersionId?: string;
}

export type TFamilySpecListResponse = IServerResponse<IFamilySpecSummary[]>;

/** An assigned approver (or committee) — BE exposes no per-approver decision status. */
export interface IFamilySpecApprover {
  username: string;
  isCommittee: boolean;
}

/** Full detail from GET /get_family_spec (a specific version, or the latest row). */
export interface IFamilySpecDetail {
  familyId?: string;
  subcategoryId?: string;
  testId: string;
  testName: string;
  testCategory: string;
  status: ITestSpecBase["status"];
  subStatus?: ITestSpecBase["subStatus"];
  testDescription: string;
  passCriteria?: string;
  justifications?: TUploadedFile[];
  profiles?: TUploadedFile[];
  /** Assigned approvers; null/absent until the draft has been submitted for approval. */
  approvers?: IFamilySpecApprover[];
  versionId: string;
}

export type TFamilySpecDetailResponse = IServerResponse<IFamilySpecDetail>;

/** One approved version from GET /get_family_spec_version_history (approved-only). */
export interface IFamilySpecVersionHistoryItem {
  versionId: string;
  summary: string;
  requestBy: string;
  approvedAt: string;
}

export type TFamilySpecVersionHistoryResponse = IServerResponse<IFamilySpecVersionHistoryItem[]>;

/** GET /get_approvers?familyId → available approver usernames. */
export type TFamilySpecApproversResponse = IServerResponse<string[]>;

export interface IFamilySpecCommitteeMember {
  username: string;
  isChair: boolean;
}

export interface IFamilySpecCommittee {
  committeeName: string;
  // BE list fields can be absent/null; guard at every use site.
  members?: IFamilySpecCommitteeMember[];
}

/** GET /get_committees?familyId → committees for the family's user group. */
export type TFamilySpecCommitteesResponse = IServerResponse<IFamilySpecCommittee[]>;

/**
 * POST /add_family_spec — creates the draft and, like edit_family_spec, auto-submits it for
 * approval when approvers + testDescription + passCriteria + justifications are all present,
 * in which case changeSummary is required.
 */
export interface TAddFamilySpecRequest {
  familyId: string;
  subcategoryId: string;
  testId: string;
  testDescription?: string;
  passCriteria?: string;
  justifications?: TUploadedFile[];
  profiles?: TUploadedFile[];
  approvers?: string[];
  isCommittee?: boolean;
  changeSummary?: string;
}

/**
 * POST /edit_family_spec — updates the draft in place (or forks a new draft from an
 * approved version). Auto-submits for approval when approvers + testDescription +
 * passCriteria + justifications are all present, in which case changeSummary is required.
 */
export interface TEditFamilySpecRequest {
  familyId: string;
  subcategoryId: string;
  testId: string;
  testDescription?: string;
  passCriteria?: string;
  justifications?: TUploadedFile[];
  profiles?: TUploadedFile[];
  approvers?: string[];
  isCommittee?: boolean;
  changeSummary?: string;
}

/** POST /discard_family_spec — deletes all draft rows (reverts to approved, or removes a draft-only spec). */
export interface TDiscardFamilySpecRequest {
  familyId: string;
  subcategoryId: string;
  testId: string;
}

// TestWaterfall

// Issue Item
export interface TIssueItem {
  issueId: string;
  description: string;
  type: TFO;
}

// Test Duration (individual test in waterfall)
export interface TTestDuration {
  testId: string;
  testName: string;
  sequence: string;
  status: TTestWaterfallStatus;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  actualStartDate?: string;
  actualEndDate?: string;
  delay?: number; // days
  duration?: number; // days
  order: number;
  issueList: TIssueItem[];
}

// Device Test Waterfall Item
export interface TTestWaterfallItem {
  // WARN:
  // realDsn: "GCC2DM002233000B". This is the device's serial number
  // buildConfig: "H-SS1" this is the config id that assigned to a list of device
  // dsn: "TABLET_SUNSTONE_HVT_1-DOT-0_H-SS1_001". This is the combination of identifiers. 001 means this is the first device in this config
  // shortId: we have a "nickname" for "H-SS1" which is like "H1".This is its shortID;
  // BUT in the frontEnd:
  //    dsn as the unique key in Radar
  //    shortId could be the combination of this "nickname" and "device number", which for example is "H1_001"
  //            or if no nickname it could be trunk of dsn which in this case is "H-SS1_001"
  dsn: string;
  realDsn: string;
  shortId: string;
  buildConfig: string;
  familyId: string;
  projectId: string;
  phaseId: string;
  buildId: string;
  testList: TTestDuration[];
}

// Backwards compatibility alias
export type TDeviceTestWaterfall = TTestWaterfallItem;
export type TDeviceTestWaterfallResponse = IServerResponse<TTestWaterfallItem[]>;

// Build Config
export interface TBuildConfigItem {
  key: string;
  name: string;
  allocation?: number;
  assigned?: number;
  // On a config-name rename, `key` carries the ORIGINAL key and `newKey` the new
  // name, so the backend renames in place instead of creating a new item.
  newKey?: string;
}
export type TBuildConfigResponse = IServerResponse<TBuildConfigItem[]>;

// Timeline Confidence
export interface TTimelineConfidenceItem {
  rate: number; // 0-1 completion/compliance rate
  issueList: string[];
}

export interface TTestWaterfallConfidenceDatapoint {
  date: string; // ISO date string
  description: string;
  predictedCompletion?: number;
  actualCompletion?: number;
}

export interface TTestWaterfallConfidence {
  confidence: number; // Overall confidence score (0-1)
  dataPoints: TTestWaterfallConfidenceDatapoint[];
  scheduleCompleteness: TTimelineConfidenceItem;
  scheduleCompliance: TTimelineConfidenceItem;
  resourceCompliance: TTimelineConfidenceItem;
}
export type TTestWaterfallTimelineOverviewResponse = IServerResponse<TTestWaterfallConfidence>;

// Edit Test Waterfall Request
export type TEditTestWaterfallRequest = TIdentifierObject & {
  dsn: string;
  testList: TTestDuration[];
};

// Edit Build Config Request
export type TEditBuildConfigRequest = TIdentifierObject & {
  allocation: TBuildConfigItem[];
};

// Copy Test Waterfall Request
export type TCopyTestWaterfallRequest = TIdentifierObject & {
  configId: string;
  copyTo: TIdentifierObject & {
    configList: string[];
  };
};

// Task Center / Data Input
export interface ITestDueItem {
  testId: string; // == testCode; used to deep-link to Data Overview for this test
  testName: string;
  dueDate: string; // ISO date string
}

/** Raw test-due item as returned by the API (uses testNameAndLevel instead of testName) */
export interface ITestDueItemRaw {
  testCode: string; // == testId, for Data Overview navigation
  testNameAndLevel: string;
  date: string; // ISO date string
}

/** Raw API shape from GET /api/get_tasks_progress (Smithy: OngoingTaskItem) */
export interface ITaskCenterProjectRaw {
  buildIdentifierIds: string; // pipe-delimited ids: "family|product|testLocation|phase|build"
  buildIdentifierNames: string; // pipe-delimited names: "familyName|productName|testLocationName|phaseName|buildName"
  realDsnCompleted: number;
  realDsnTotal: number;
  testInputsCompleted: number;
  testInputsTotal: number;
  testsDueSoon: ITestDueItemRaw[];
}

export type TTaskCenterRawResponse = IServerResponse<ITaskCenterProjectRaw[]>;

/** Parsed shape used by the UI components */
export interface ITaskCenterProject {
  projectId: string;
  projectName: string;
  phaseId: string;
  buildId: string;
  familyId: string;
  testLocationId: string;
  realDsnProgress: {
    completed: number;
    total: number;
  };
  testInputs: {
    completed: number;
    total: number;
  };
  testsDueSoon: ITestDueItem[];
}

export type TTaskCenterResponse = IServerResponse<ITaskCenterProject[]>;

// Test Summary / Reports

// --- Test Header (fetch_TestSummary_Data) ---
export interface ITestSummaryData {
  testId: string;
  testName: string;
  testCategory: string;
  testLevel: string;
  testDescription: string;
  testSchedule: {
    startDate: string;
    endDate: string;
  };
  overallFailureRate: string;
  testConclusion: string;
  status: "ongoing" | "completed" | "pending";
}
export type TTestSummaryResponse = IServerResponse<ITestSummaryData>;

// --- Issue Summary Tab (GET /api/get_issue_summary) ---
export interface IIssueSummaryPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface IIssueSummaryItem {
  issueId: number;
  issueType: string; // e.g. "Failure | Mechanical"
  isFailure: boolean;
  issue: string; // e.g. "#2871: Rear cover - Corner sharp deformation"
  failingComponent: string;
  failureMode: string;
  recovery: string;
  category: string;
  jiraUrl: string;
  failedReadoutCount: number;
  failedDeviceCount: number;
  isNewIssue: boolean;
  isNewFailedReadout: boolean;
  isNewFailedDevice: boolean;
  createdTs: string;
}

export interface IIssueSummaryData {
  pagination: IIssueSummaryPagination;
  issueSummaryList: IIssueSummaryItem[];
}
export type TIssueSummaryResponse = IServerResponse<IIssueSummaryData>;

// --- Issue Detail (GET /api/get_issue_detail) ---
export interface IIssueDetailFailureRateItem {
  configName: string;
  failedCount: number;
  totalCount: number;
  failureRate: string; // e.g. "3F/10T"
  radarPlusId: string;
  isNew: boolean;
}

export interface IIssueDetailFailedDevice {
  dsn: string;
  shortId: string; // human-readable device short id (e.g. "E1-HDMI2_006")
  realDsn: string;
  failedReadoutOrder: number;
  readoutName: string; // e.g. "height (m)-0.5 : cycles-20"
  isNew: boolean;
  faStatus: string | null;
  photoUrls: string[];
}

export interface IIssueDetail {
  issueId: number;
  issueType: string;
  description: string;
  jiraUrl: string;
  untrackedFailedDevices: number;
  failureRateSummary: IIssueDetailFailureRateItem[];
  failedDevicePhotos: {
    pagination: IIssueSummaryPagination;
    items: IIssueDetailFailedDevice[];
  };
}
export type TIssueDetailResponse = IServerResponse<IIssueDetail>;

// --- Test Result Tab (GET /api/get_readout_data_by_test) ---
export interface IReadoutResultItem {
  readoutOrder: number;
  readoutLabel: string; // e.g. "Impact-48"
  result: "1" | "0" | "-1" | null; // "1"=Pass, "0"=Fail (show issueIds), "-1"=N/A
  issueIds: number[];
}

export interface IReadoutIssueItem {
  issueId: number;
  description: string;
  type: string; // "failure" or "observation"
}

export interface ITestResultDeviceRow {
  dsn: string;
  realDsn: string;
  deviceDisplayName: string; // e.g. "E25NP-R_015"
  configName: string;
  overallStatus: string; // "PASS", "FAIL", or raw status
  hasCmApproved: boolean; // true once a CM has approved the (failed) result
  issues: IReadoutIssueItem[];
  readoutResults: IReadoutResultItem[];
}

export interface IReadoutDataByTest {
  orientations: number[]; // sorted list of readout order numbers (column headers)
  failedCount: number;
  totalCount: number;
  failureRate: string; // e.g. "2F/10T"
  deviceResults: ITestResultDeviceRow[];
}
export type TReadoutDataByTestResponse = IServerResponse<IReadoutDataByTest>;

// --- Grouping Sequences (GET /api/get_grouping_sequences) ---
export interface IGroupingSequence {
  groupingSequence: string;
}
export type TGroupingSequencesResponse = IServerResponse<IGroupingSequence[]>;

// --- Attachments Tab (GET /api/get_attachments_by_test) ---
export interface IAttachmentItem {
  presignedUrl: string;
  fileName: string;
  fileType: "PDF" | "IMAGE" | "OTHER";
  readoutId: number | null;
  issueId: number | null;
}

export interface IDeviceAttachment {
  radarPlusId: string;
  attachments: IAttachmentItem[];
}

export interface IAttachmentsByTest {
  deviceAttachments: IDeviceAttachment[];
}
export type TAttachmentsByTestResponse = IServerResponse<IAttachmentsByTest>;
// Milestone type for edit milestones API
export interface TMilestone {
  name: string;
  date: string; // ISO date string (YYYY-MM-DD)
}

// Edit Milestones Request
export type TEditMilestonesRequest = TIdentifierObject & {
  milestones: TMilestone[];
};

// Get Holidays Response (non-working days)
export type TGetHolidaysResponse = IServerResponse<string[]>; // Array of ISO date strings (YYYY-MM-DD)

// Edit Holidays Request (non-working days)
export type TEditHolidaysRequest = TIdentifierObject & {
  dates: string[]; // Array of ISO date strings (YYYY-MM-DD)
};

// Edit Test Timeline Request (for editing timeline of a single DSN)
// A waterfall test instance is identified by (testId, sequence): the same testId can appear under
// multiple sequences, so sequence is required for the BE to target the correct row.
// startDate and endDate are BOTH optional: the BE model (EditTestTimelineItem) marks every
// field optional and defines dates as DateString = @pattern("^(\d{4}-\d{2}-\d{2})?$") whose
// doc says "Empty string is allowed and treated as null/unset." A test with no dates is still
// sent (identifiers only) so the BE receives the full test count and auto-calculates downstream
// dates from test duration. Empty dates are OMITTED rather than sent as partial/invalid strings.
export type TEditTestTimelineTest = Pick<TTestDuration, "testId" | "sequence"> & {
  dsn: string;
  startDate?: string;
  endDate?: string;
};

export type TEditTestTimelineRequest = TIdentifierObject & {
  tests: TEditTestTimelineTest[];
};

// Real DSN / Data Input
export interface IRealDSNItem {
  id: string; // e.g., "D1F-Buzze1_001"
  dsn: string; // display DSN rendered in the "ID" column (cm_label or DSN suffix)
  shortId: string; // canonical device short id from the backend (e.g. "E1-HDMI2_006")
  realDsn: string; // e.g., "GN42HE01315700AV"
  receivedDate: string; // ISO date string, e.g., "2025-09-21"
  buildConfig: string; // e.g., "D1F-Buzze1"
  isEditable?: boolean; // when false, edit actions are disabled for this DSN
}

export type TRealDSNListResponse = IServerResponse<IRealDSNItem[]>;

export type TUpdateRealDSNRequest = TIdentifierObject & {
  id: string;
  realDsn?: string;
  receivedDate?: string;
};

export type TBulkUploadRealDSNRequest = TIdentifierObject & {
  file: File;
};

export interface IRealDSNChange {
  dsn: string;
  hasRealDsnConflict?: boolean; // true when the same realDsn appears in multiple rows in the file
  isEditable: boolean;
  isStringFormatValid: boolean;
  message?: string; // per-item status message from the API
  newRealDsn: string;
  newReceivedDate: string;
  oldRealDsn: string;
  oldReceivedDate: string;
}

export interface IBulkUploadResult {
  changes: IRealDSNChange[];
  code: number;
  isSuccess: boolean;
  message: string;
  updateToDatabase: boolean; // true = all valid and saved; false = errors found, not saved
}

export type TBulkUploadRealDSNResponse = IServerResponse<IBulkUploadResult>;

// ── Import Waterfall ────────────────────────────────────────────────────────
// Mirrors WaterfallImportChangeItem in import_waterfall.smithy (BE commit 42e3ff1).

export type TChangeType = "ADD" | "REMOVE" | "MOVE" | "DSN_REMOVE" | "UNCHANGED";

export interface IWaterfallImportChange {
  dsn: string;
  shortId: string;
  testId: string;
  testName: string;
  sequence: string;
  oldOrder: number;
  newOrder: number;
  changeType: TChangeType;
  isValid: boolean;
  isEditable: boolean;
  hasDuplicateSequence: boolean;
  message: string;
}

export interface IWaterfallImportResult {
  changes: IWaterfallImportChange[];
  updateToDatabase: boolean; // true = all valid and committed; false = errors found, not saved
}

export type TImportWaterfallResponse = IServerResponse<IWaterfallImportResult>;

export type TImportWaterfallRequest = TIdentifierObject & {
  file: File;
};

// ── Test Settings ─────────────────────────────────────────────────────────────

/** Full equipment entry as required by the backend Smithy model */
export interface IEquipmentEntry {
  equipmentName: string;
  equipmentId: string;
  category: string;
  location: string;
}

/** Inline readout summary returned within each sequence from get_grouping_sequences */
export interface ISequenceReadoutSummary {
  readoutOrder: number;
  isMajorReadout: boolean;
  typeValue: string; // concatenated display label; ambiguous to re-split — prefer keyValuePairs
  keyValuePairs?: IReadoutKeyValuePair[]; // structured pairs — authoritative source for editing
}

export interface ITestSettingsSequence {
  sequenceId?: number; // numeric DB ID — used for check/measurement mapping and readout API calls
  groupingSequence: string; // sequence name (primary key)
  readoutCount: number;
  majorReadoutCount: number;
  defaultDuration: number; // days
  skipNonWorkingDays: boolean;
  averageCapacity: number; // per equipment per day
  equipmentCount: number;
  readouts?: ISequenceReadoutSummary[]; // inline readout summaries from get_grouping_sequences
  equipmentList?: IEquipmentEntry[]; // optional — not returned by GetGroupingSequences
  hasData?: boolean; // optional — not returned by GetGroupingSequences
}

export type TTestSettingsSequenceListResponse = IServerResponse<ITestSettingsSequence[]>;

export type TAddEditSequenceRequest = TIdentifierObject & {
  sequenceId?: number; // edit only: identifies the row so groupingSequence can carry a renamed value
  groupingSequence: string; // sequence name (the new name when renaming on edit)
  defaultDuration: number;
  skipNonWorkingDays: boolean;
  averageCapacity: number;
  equipmentList: IEquipmentEntry[];
};

// ── GetCopySequenceOptions ────────────────────────────────────────────────────

export interface ICopySequenceSourceItem {
  groupingSequence: string;
  readoutCount: number;
  majorReadoutCount: number;
  isSelectable: boolean;
  reason?: string; // reason why not selectable (e.g. destination already has data)
}

type TCopySequenceDestination = Required<Pick<TIdentifierObject, "familyId" | "projectId" | "testLocationId" | "phaseId" | "buildId" | "testId">>;

export type TCopySequenceOptionsRequest = TCopySequenceDestination & {
  sourceProjectId: string;
  sourceTestLocationId: string;
  sourcePhaseId: string;
  sourceBuildId: string;
  sourceTestId: string;
};

export interface TCopySequenceOptionsResponse {
  isSuccess: boolean;
  code: number;
  message?: string;
  sourceSequences: ICopySequenceSourceItem[]; // at root level per API spec
}

export type TCopySequenceRequest = TCopySequenceDestination & {
  sourceProjectId: string;
  sourceTestLocationId: string;
  sourcePhaseId: string;
  sourceBuildId: string;
  sourceTestId: string;
  selectedSequences: string[]; // sequence names
};

export type TCopySequenceResponse = IServerResponse<{ copiedCount: number; skippedSequences: string[] }>;

// ── GetDeleteSequenceOptions ──────────────────────────────────────────────────

export interface IDeleteSequenceItem {
  groupingSequence: string;
  isDeletable: boolean;
  reason?: string; // reason why not deletable (e.g. has data)
}

export interface TDeleteSequenceOptionsResponse {
  isSuccess: boolean;
  code: number;
  message?: string;
  sequences: IDeleteSequenceItem[]; // at root level per API spec
}

export type TDeleteSequenceRequest = TIdentifierObject & {
  selectedSequences: string[]; // sequence names
};

// ── GetDeleteReadoutOptions ───────────────────────────────────────────────────

export interface IDeleteReadoutItem {
  order: number;
  hasData: boolean;
  isDeletable: boolean;
}

export interface TDeleteReadoutOptionsResponse {
  isSuccess: boolean;
  code: number;
  message?: string;
  readouts: IDeleteReadoutItem[]; // at root level per API spec
}

export type TDeleteSequenceResponse = IServerResponse<{ deletedCount: number; skippedSequences: string[] }>;

export interface ITestSettingsReadout {
  readoutId: string;
  order: number;
  readoutOrder?: number; // from get_grouping_sequences inline readouts
  type: string; // from master list
  value: string;
  typeValue?: string; // combined "type-value" from get_grouping_sequences; ambiguous — prefer keyValuePairs
  keyValuePairs?: IReadoutKeyValuePair[]; // structured pairs from get_grouping_sequences — authoritative for editing
  majorReadout: boolean;
  isMajorReadout?: boolean; // from get_grouping_sequences inline readouts
  hasData: boolean;
}

export type TTestSettingsReadoutListResponse = IServerResponse<ITestSettingsReadout[]>;

export type TAddEditReadoutRequest = Omit<TIdentifierObject, "sequenceId"> & {
  sequenceId: number;
  readoutId?: string; // omit for Add
  order: number;
  isMajorReadout: boolean;
  keyValuePairs: IReadoutKeyValuePair[]; // structured pairs (backend AddReadout/EditReadout shape)
};

export type TReadoutTypeMasterListResponse = IServerResponse & { keys: string[] };

// ── Check Mapping (spec: get_readout_checks / edit_readout_checks / reset_readout_checks) ──

export interface ICheckAssignmentItem {
  checkId: number;
  checkCode: string; // e.g. "AFT", "ARF", "VI", "FC"
  checkName: string; // e.g. "AFT Check", "Visual Inspection"
  hasData?: boolean; // true when this (readout, check) pair already has recorded result data — its checkbox is locked so it can't be unassigned
}

export interface IReadoutCheckRow {
  readoutOrder: number;
  readoutLabel: string; // e.g. "voltage : +8kV | location : L1 | number of zaps : 10"
  isMajorReadout: boolean;
  hasData: boolean; // true once readout data has been entered — checkboxes for this row are locked in edit mode to prevent data loss
  assignedChecks: ICheckAssignmentItem[]; // checks assigned to this readout
}

/** Response from get_readout_checks — allChecks is at root level, data is the rows array */
export interface TTestSettingsCheckMappingResponse {
  isSuccess: boolean;
  code: number;
  message?: string;
  data: IReadoutCheckRow[]; // per-readout rows
  allChecks: ICheckAssignmentItem[]; // master list of available checks at root level
}

export interface ITestSettingsCheckMappingData {
  data: IReadoutCheckRow[]; // per-readout rows
  allChecks: ICheckAssignmentItem[]; // master list of available checks
}

export interface TUpdateCheckMappingRequest {
  sequenceId: number;
  updates: { readoutOrder: number; checkIds: number[] }[];
}

// ── Measurement Mapping (spec: get_readout_measurements / edit_readout_measurements) ──

/**
 * The `type` discriminator persisted on a measurement mapping. These are the only three values the
 * backend reads or writes; it derives the type from `readoutOrder` on save, so it is informational
 * on the response rather than something the client chooses.
 */
export type TMeasurementMappingType = "PRE-TEST" | "DEFAULT" | "FINAL";

/** Sentinel `readoutOrder` for the Pre-Test bucket — stored as `test_readout_id IS NULL`. */
export const PRE_TEST_READOUT_ORDER = 0;

/** Sentinel `readoutOrder` for the Final Readout bucket — stored as `test_readout_id IS NULL`. */
export const FINAL_READOUT_ORDER = -1;

export interface IReadoutMeasurementMappingItem {
  mappingId: number;
  readoutOrder: number;
  measurementComponentMappingId: number;
  type: TMeasurementMappingType;
  dataFilled: boolean;
}

export interface IReadoutMeasurementRow {
  readoutOrder: number;
  readoutLabel: string;
  mappings: IReadoutMeasurementMappingItem[];
}

export interface IAvailableMeasurement {
  measurementComponentMappingId: number;
  measurementName: string;
  componentName: string;
  unit: string;
  upperLimit: number;
  lowerLimit: number;
  order: number;
}

export interface IPrePostTestSummaryItem {
  measurementComponentMappingId: number;
  hasPreTest: boolean;
  hasPostTest: boolean;
}

export interface ITestSettingsMeasurementMappingData {
  readouts: IReadoutMeasurementRow[];
  availableMeasurements: IAvailableMeasurement[];
  prePostSummary: IPrePostTestSummaryItem[];
}

export type TTestSettingsMeasurementMappingResponse = IServerResponse<ITestSettingsMeasurementMappingData>;

export interface TUpdateMeasurementMappingRequest {
  sequenceId: number;
  mappings: { readoutOrder: number; measurementComponentMappingId: number; type: TMeasurementMappingType }[];
}

export interface ITestSettingsTestVariable {
  variableKey: string;
  variableName: string;
  variableValues: string[];
  applyStatus: boolean;
}

export type TTestSettingsTestVariableResponse = IServerResponse<ITestSettingsTestVariable[]>;

export type TUpdateTestVariableApplyStatusRequest = TIdentifierObject & {
  variableKey: string;
  applyStatus: boolean;
};

export interface ITestSettingsEquipment {
  equipmentId: string;
  equipmentName: string;
  category?: string;
  location: string; // e.g. "Taiyuan(VN)", "Nanchang(CN)"
}

export type TTestSettingsEquipmentListResponse = IServerResponse<ITestSettingsEquipment[]>;

/** Equipment item returned by GetEquipmentListForSequence (proxied from Test Fidelity) */
export interface ITestFidelityEquipmentItem {
  equipmentName: string;
  category: string;
  location: string; // CM test location, e.g. "Dalian(CN)"
  isAvailable: boolean; // approved and available per Test Fidelity calibration/checklist/SOP
}

export interface TTestFidelityEquipmentListResponse {
  isSuccess: boolean;
  code: number;
  message?: string;
  equipments: ITestFidelityEquipmentItem[]; // at root level per API spec
}

// Test Status
export type TTestStatusMode = "byTest" | "byReadout";

export interface TTestStatusIssue {
  issueId: string; // e.g., "#3214"
  type: TFO;
  description: string; // e.g., "Device - ADT StationCTF fail. this is the combination of component and failure part
  failureCount: number; // Number of failures
  changedCount?: number;
}

export interface TTestStatusCompletion {
  plan: number; // Count of planned tests
  cancel: number; // Count of cancelled tests
  ongoing: number; // Count of ongoing tests
  complete: number; // Count of completed tests
}

export interface ITestStatusItem {
  testId: string;
  testName: string; // e.g., "Baseline Check Test - Lv1"
  completion: TTestStatusCompletion;
  completionRate: number; // 0-100 percentage
  failureCount: number; // Number of failures
  totalCount: number; // Total test count
  etc: string; // Estimated Time to Completion, ISO date string, e.g., "2026-01-04"
  issues: TTestStatusIssue[];
}

export type TTestStatusListResponse = IServerResponse<ITestStatusItem[]>;

// ── FDDD (Failure Data Deep Dive) ─────────────────────────────────────

/** A single option returned by the cascading filter API for any dimension. */
export interface IFDDDDimensionOption {
  id: string;
  name: string;
  matchedField?: string;
}

/** A recursive tree node for the full identifier tree (Family → Project → Location → Phase → Build).
 *  Uses `key`/`name` matching the BE response from GetCascadingFilterOptions. */
export interface IIdentifierTreeNode {
  key: string;
  name: string;
  children?: IIdentifierTreeNode[];
}

/** Date range filter for Issue Found Date dimension. */
export interface IFDDDDateRange {
  startDate: string | null;
  endDate: string | null;
}

/**
 * Auto-generated "{dimensionKey}Ids" fields from DimensionKeyMap.
 * Excludes ISSUE_FOUND_DATE (handled separately as a date range).
 */
type TFDDDDimensionIdFields = {
  [K in TDimensionValue as K extends typeof DimensionKeyMap.ISSUE_FOUND_DATE ? never : `${K}Ids`]?: string[];
};

/**
 * Auto-generated "{dimensionKey}Options" fields from DimensionKeyMap.
 * Excludes ISSUE_FOUND_DATE (handled separately as a date range).
 */
type TFDDDDimensionOptionFields = {
  [K in TDimensionValue as K extends typeof DimensionKeyMap.ISSUE_FOUND_DATE ? never : `${K}Options`]: IFDDDDimensionOption[];
};

/** A phase-build pair with individual fields. */
export interface IFDDDPhaseBuild {
  phaseId: string;
  buildId: string;
}

/** A single project paired with its selected phase-builds, with parsed individual fields. */
export interface IFDDDProjectSelection {
  familyId: string;
  projectId: string;
  testLocationId: string;
  phaseBuilds: IFDDDPhaseBuild[];
}

/**
 * Request payload for POST /GetCascadingFilterOptions.
 * Uses paired selections (each project with its own phase-builds) instead of flat arrays.
 * Optional dimension ID fields are auto-derived from DimensionKeyMap.
 */
export type IFDDDCascadingFilterRequest = TFDDDDimensionIdFields & {
  /** Paired project → phase-build selections with individual parsed fields */
  selections: IFDDDProjectSelection[];
  issueFoundDateRange?: IFDDDDateRange;
  /** Dimension types to fetch options for (from DimensionKeyMap values). Only fetches options for the specified dimensions. */
  dimensionTypes?: TDimensionKey[];
  pageSize?: number;
};

/**
 * Response from POST /GetCascadingFilterOptions.
 * Dimension option fields are auto-derived from DimensionKeyMap.
 */
export type IFDDDCascadingFilterResponse = TFDDDDimensionOptionFields & {
  issueFoundDateRange: { minDate: string; maxDate: string };
  /** Full 5-level identifier tree (Family → Project → Location → Phase → Build).
   *  Only present when dimensionTypes includes "IDENTIFIER_TREE". */
  identifierTreeOptions?: IIdentifierTreeNode[];
};

export type TFDDDCascadingFilterResponse = IServerResponse<IFDDDCascadingFilterResponse>;

/** Attachment on a failure rate node (pre-signed image URL). */
export interface IFDDDAttachment {
  filename: string;
  preSignedURL: string;
  s3Path: string;
}

/** A node in the hierarchical failure rate tree. */
export interface IFDDDFailureRateNode {
  id: string;
  name: string;
  dimension: string;
  total: number;
  failures: number;
  failureRate: number;
  children: IFDDDFailureRateNode[];
  /** Optional attachments (photos) associated with this node. */
  attachments?: IFDDDAttachment[];
}

/**
 * Request params for GET /api/get_failure_rate_summary.
 * Uses paired selections (same as cascading filter) for multi-project / multi-phase-build support.
 * Dimension ID fields are auto-derived from DimensionKeyMap.
 */
export type IFDDDFailureRateSummaryRequest = TFDDDDimensionIdFields & {
  /** Paired project → phase-build selections with individual parsed fields */
  selections: IFDDDProjectSelection[];
  /** Ordered list of dimension keys defining the grouping hierarchy */
  path: string[];
  startDate?: string;
  endDate?: string;
};

export type TFDDDFailureRateSummaryResponse = IServerResponse<IFDDDFailureRateNode[]>;

// ── Presets (universal, feature-parameterized) ────────────────────────

/** A single preset item — internal representation keeps `path` as string[].
 *  The API transmits the field as `requestParameter` (a JSON-stringified string);
 *  serialization / deserialization happens in the service layer. */
export interface IPresetItem {
  uuid?: string;
  path?: string[];
  description?: string;
  /** When true, this preset is a system default and cannot be edited or deleted by the user. */
  isDefault?: boolean;
}

export type TPresetListResponse = IServerResponse<IPresetItem[]>;
export type TPresetResponse = IServerResponse<IPresetItem>;

// ── Operator Management (admin) ───────────────────────────────────────

export type TOperatorListResponse = IServerResponse<IOperator[]>;
export type TOperatorCreateRequest = ICreateOperatorRequest;
/** Create & reset-password return a one-time temporary password the admin must convey to the operator. */
export interface IOperatorPasswordResult {
  username: string;
  temporaryPassword: string;
}
export type TOperatorPasswordResponse = IServerResponse<IOperatorPasswordResult>;

// ── ReadoutInput (Data Input → Readout Detail) ───────────────────────

// -- Check Issue (within a readout check) --
// Stored shape after the user picks an issue from the IssueList dropdown:
// the master record fields (denormalized snapshot) plus per-instance edits.
export interface IReadoutCheckIssue {
  // Master record (subset of IIssueListItem). issueId 0/-1 means "unset".
  issueId: number;
  details: string;
  failingComponent?: string;
  location?: string;
  failureMode?: string;
  recovery?: TIssueRecovery;
  recoverySteps?: string;
  severity?: TIssueSeverity;
  category?: TIssueCategory;
  translation?: TIssueTranslation;
  isMappedWithChecks?: boolean;
  // Per-instance edits.
  comment?: string;
  attachment: TUploadedFile[];
  // ESD-only: which zaps this issue covers, and whether the test was aborted.
  failedZaps?: number[];
  testAbort?: boolean;
}

// -- Check (within a readout) --
export interface IReadoutCheck {
  id: number;
  key: string;
  name: string;
  checkCategory: string; // e.g. "default", "fatp", "esd" — may be comma-separated for multi-category
  status?: string; // e.g. "passfailna"
  issue?: IReadoutCheckIssue[];
  fatp?: IReadoutFATPLogOption; // selected FATP genealogy record (single object); undefined when unmapped
}

// -- Measurement (within a readout) --
export interface IReadoutMeasurement {
  id: number;
  component: string;
  name: string;
  // Optional: computed rows (CCP/CRP) arrive without a unit, and Smithy does not
  // mark it @required. MeasurementRow already renders the label unit-less.
  unit?: string;
  // Display order from the product configuration (product_measurement_setting."order").
  // Sorted on by MeasurementSection so measured and computed rows cannot interleave
  // arbitrarily. Optional: absent on the test-spec path and until the BE ships.
  order?: number;
  avgMeasurementNum?: number; // number of trial inputs
  measurementAttemptValues?: number[]; // pre-filled values per trial
  // If `equation` is present, this measurement is computed (read-only).
  // The expression is JS-form (e.g. "Math.sqrt(a*a + b*b)") and is evaluated
  // client-side against averages of the parameter measurements.
  equation?: string;
  equationParameters?: Record<string, number>; // variable name -> source measurementId
  calculatedValue?: number; // computed value for equation measurements
}

// -- Key-Value Pair (readout sub-header, e.g. "Voltage: +8kV") --
export interface IReadoutKeyValuePair {
  key: string;
  value: string;
}

// -- Readout --
export interface IReadout {
  order: number; // -1 = Pre-Test, 1+ = Readout#N
  id: number;
  readoutId: number; // BE readout id (== id; the save-payload key)
  readoutDataId: number; // BE readout-data id; -1 for a new/unsaved readout
  result: string; // overall readout result, e.g. "pass" | "fail" | "na"
  isFinal: boolean;
  isPassFailNull?: string; // TBD — pass/fail/null status indicator
  keyValuePair: IReadoutKeyValuePair[];
  measurements: IReadoutMeasurement[];
  checks: IReadoutCheck[];
  // ESD-only: which zaps sparked during this readout (1..numberOfZaps).
  sparkingZaps?: number[];
}

// -- Test Variable (selected value for a variable) --
export interface ITestVariable {
  key: string;
  name: string;
  value: string;
}

// -- Full Test Data (primary API response) --
export type IReadoutTestData = TIdentifierObject & {
  dsn: string;
  id: number; // numeric test id (echoed back in the save payload)
  testId: string;
  testName: string; // human-readable test name, e.g. "ESD Air Discharge Test - L1"
  sequenceId: string; // test sequence identifier, e.g. "1"
  // BE-formatted breadcrumb label combining family / project / cmLocation /
  // phase / build, e.g. "EINK / Paloma / Shenzhen / DVT 1.0".
  displayName: string;
  testStatus: TReadoutTestStatus;
  testType: string; // e.g. "default", "esd", "ball_impact" — drives special treatment section
  testVariable: ITestVariable[];
  readouts: IReadout[];
};

export type TReadoutTestDataResponse = IServerResponse<IReadoutTestData>;

// -- Save Readout Data (POST /save_readout_data) --
// The save body carries a SINGULAR `readout` (the currently edited one) instead
// of the `readouts[]` array the GET returns. Module-internal-only readout fields
// (`id`, `isPassFailNull`) are dropped — `readoutId` / `readoutDataId` are the
// BE-facing identifiers.
//
// ESD wire-shape divergence from the UI shape (Smithy SaveReadoutCheckIssue):
//   - readout `sparkingZaps` and issue `failedZaps` are scalar CSV Strings on the
//     wire ("1,2,3"), NOT number[].
//   - the BE field is `continueTesting: Boolean` (the inverse of the UI's
//     `testAbort`); there is no `testAbort` on the wire.
export type TSaveReadoutCheckIssue = Omit<IReadoutCheckIssue, "failedZaps" | "testAbort"> & {
  failedZaps?: string;
  continueTesting?: boolean;
};

export type TSaveReadoutCheck = Omit<IReadoutCheck, "issue"> & {
  issue?: TSaveReadoutCheckIssue[];
};

export type TSaveReadoutReadout = Omit<IReadout, "id" | "sparkingZaps" | "isPassFailNull" | "checks"> & {
  checks: TSaveReadoutCheck[];
  // ESD-only: which zaps sparked, as a CSV String ("1,2,3") on the wire (like failedZaps).
  sparkingZaps?: string;
};

export type TSaveReadoutRequest = Omit<IReadoutTestData, "readouts"> & {
  readout: TSaveReadoutReadout;
};

// -- FATP Genealogy / Log Options (for FATP log mapping dropdown) --
// Wire contract: BE operation GetFatpGenealogy (GET /api/get_fatp_genealogy),
// item shape FatpGenealogyItem. testResult is the string "pass" | "fail"
// (FATPGenealogyService normalizes the raw FATP result). serialId is the log
// identifier the FE maps onto a readout check. The whole selected option is
// stored on `check.fatp` and echoed back verbatim in the save payload, so every
// field is required.
export interface IReadoutFATPLogOption {
  serialId: string;
  testResult: string; // "pass" | "fail"
  startTime: string;
  errorCode: string;
  process: string;
  station: string;
}

export type TReadoutFATPLogOptionsResponse = IServerResponse<IReadoutFATPLogOption[]>;

/**
 * Request for the Readout Input FATP candidate log download.
 *
 * `dsns` is a repeated query param (`?dsns=A&dsns=B`) matching the Smithy `StringList`, so the call
 * must use `repeatedKeyParamsSerializer`. `checkCode` is camelCase on both the wire and the member,
 * matching this operation's Smithy contract — note `GetFatpGenealogy` spells the same concept
 * `checkcode`, so do not copy that call's params object.
 *
 * The BE caps devices at one (MAX_DSNS), so callers send exactly one DSN even though the contract
 * takes a list.
 */
export type TFATPCandidateLogsRequest = TIdentifierObject & {
  dsns: string[];
  checkCode: string;
};

// ── FATP Log Link drawer (DataOverview) ──────────────────────────────────────
// The drawer's "station" is a FATP check (STATION === checkCode app-wide). The
// check list comes from /get_fatp_checks; genealogy records come from
// /get_batch_fatp_genealogy; the mapping is persisted via /save_batch_fatp_mapping.
// NOTE: checkId and checkCode are the SAME string value — the BE simply names the
// field `checkId` on the genealogy input and `checkCode` on the save input.

// -- FATP Check options (source: /get_fatp_checks, fatp.smithy FatpCheckItem) --
// Wire shape uses @jsonName: checkCode → "key", checkName → "name".
export interface IFATPCheckOption {
  checkId: number; // BE numeric row id (distinct from the checkCode string below)
  key: string; // checkCode — the value sent as checkId (fetch) / checkCode (save)
  name: string; // checkName — display label
}
export type TFATPCheckOptionsResponse = IServerResponse<IFATPCheckOption[]>;

// -- Genealogy record (fatp.smithy FatpGenealogyItem) --
// testResult is a raw BE String (e.g. "PASS"/"FAIL"/error text), not a 0|1 union.
export interface IFatpGenealogyItem {
  serialId: string;
  startTime: string;
  testResult: string;
  errorCode: string;
  process: string;
  station: string;
}

// One device's genealogy records (fatp.smithy BatchFatpGenealogyDeviceItem).
export interface IBatchFatpGenealogyDevice {
  dsn: string;
  shortId: string;
  fatp: IFatpGenealogyItem[];
}

// POST /get_batch_fatp_genealogy request. Field names mirror the BE wire keys:
// `checkId` carries the checkCode value. fromDate/toDate are sent in anticipation
// of upcoming BE date filtering (not yet honored by the deployed endpoint).
export interface TFATPGenealogyRequest extends TIdentifierObject {
  checkId: string;
  // Sequence scope (from the locked row data). Sent ahead of the BE adding the
  // field; the deployed endpoint ignores unknown params until then.
  sequenceId: string;
  testReadoutId: number;
  dsns: string[];
  fromDate: string;
  toDate: string;
}
export type TFATPGenealogyResponse = IServerResponse<IBatchFatpGenealogyDevice[]>;

// POST /save_batch_fatp_mapping request (fatp.smithy SaveBatchFatpMappingInput).
// Extends the identifier block (BuildQueryBaseRequest ≈ TIdentifierObject). One
// top-level issueId applies to the whole batch (required when any picked record fails).
export interface ISaveBatchFatpMappingEntry {
  dsn: string;
  fatp: IFatpGenealogyItem[];
}
export interface TSaveBatchFatpMappingRequest extends TIdentifierObject {
  checkCode: string;
  // Sequence scope (from the locked row data). Sent ahead of the BE adding the field.
  sequenceId: string;
  testReadoutId: number;
  issueId: number;
  fatp: ISaveBatchFatpMappingEntry[];
}
export type TSaveBatchFatpMappingResponse = IServerResponse<unknown>;

// -- Final Check (defines a check that appears on final readouts) --
export interface IReadoutFinalCheck {
  id: number;
  key: string;
  name: string;
  // Category the check renders under — "default" (Checks section) or "fatp"
  // (FATP section). The BE tags final-only checks (e.g. aft, arf, pdot) as
  // "fatp"; carry it through the merge so the client "Mark as final" path
  // splits sections the same way a BE reload does.
  checkCategory?: string;
}

// -- Final Measurement (defines a measurement that appears on final readouts) --
export interface IReadoutFinalMeasurement {
  id: number;
  component: string;
  name: string;
  unit?: string;
  // Display order from the product configuration (product_measurement_setting."order").
  // The BE already sorts by it; we keep it so a client-side merge into an existing readout
  // can restore the same order instead of appending. Optional: absent until the BE ships.
  order?: number;
  avgMeasurementNum?: number;
  equation?: string;
  equationParameters?: Record<string, number>;
}

// -- Variable Selection (replaces IReadoutTestVariableOption) --
export interface IReadoutVariableSelection {
  key: string;
  name: string;
  values: string[];
}

// -- Metadata response (consolidates final checks, final measurements, variable selections) --
export interface IReadoutMetadata {
  finalChecks: IReadoutFinalCheck[];
  finalMeasurements: IReadoutFinalMeasurement[];
  variableSelections: IReadoutVariableSelection[];
}

export type TReadoutMetadataResponse = IServerResponse<IReadoutMetadata>;

// -- Device List (for Previous/Next DSN navigation) --
export interface IReadoutDeviceListItem {
  dsn: string;
  shortId: string;
}

export type TReadoutDeviceListResponse = IServerResponse<IReadoutDeviceListItem[]>;

// Test Assignment / Data Input
export interface ITestAssignmentItem {
  testCode: string;
  testNameAndLevel: string;
  testExecutionCompleted: number;
  testExecutionTotal: number;
  overdueToStart: number;
  awaitingReview: number;
  nextDueDate: string; // ISO date string, e.g., "2025-12-10"
  operator: string[];
}

export type TTestAssignmentListResponse = IServerResponse<ITestAssignmentItem[]>;

export type TUpdateTestAssignmentRequest = TIdentifierObject & {
  usernames: string[];
  testCodeIds: string[];
};

export interface TAssignOperatorsResponse {
  isSuccess: boolean;
  code: number;
  message: string;
  totalAssignments: number;
}

export interface IOPOption {
  firstName: string;
  lastName: string;
  username: string;
}

export type TOPListResponse = IServerResponse<IOPOption[]>;

// ── FA Tracker ─────────────────────────────────────────────────────────────

export type TFAStatus = "IN_PROGRESS" | "CLOSED";

// Summary Cards (from GetFaSummary)
export interface IFATrackerSummary {
  closeRate: number; // percentage, e.g. 37.5
  pendingCount: number;
  overdueCount: number;
  incompleteClosureCount: number;
}

// Pagination (shared)
export interface IFAPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

// FA Log table row (from GetFaLog)
export interface IFALogEntry {
  id: number;
  faCaseNo: string;
  status: TFAStatus;
  jiraNumber: string | null;
  issueTestDescription: string;
  buildIdentifier: string;
  latestSummary: string | null;
  rootCause: string | null;
  correctiveActions: string | null;
  updatedTs: string; // ISO timestamp
}

export interface IFALogListData {
  items: IFALogEntry[];
  pagination: IFAPagination;
}

export type TFALogListResponse = IServerResponse<IFALogListData>;

// New Findings table row (from GetNewFindings)
export interface INewFinding {
  id: number;
  issueId: number; // FK into radar_issue_list; drives the "#{issueId}" prefix
  foundDate: string; // ISO timestamp
  issueDescription: string;
  testName: string;
  dsn: string;
  buildIdentifier: string;
  pendingDays: number;
  isWaived: boolean;
  checkpoint: string | null;
}

export interface INewFindingsListData {
  items: INewFinding[];
  pagination: IFAPagination;
}

export type TNewFindingsListResponse = IServerResponse<INewFindingsListData>;

// New Findings server-side filter (from GetNewFindingsFilterOptions)
// `id` is the stable key GetNewFindings filters on; `name` is the display string. The two differ
// for dimensions whose display text is composed rather than stored (Issue/DSN/Test/Build), so the
// filter must carry the id and only show the name. Two options in one dimension may share a name
// but never an id.
export interface INewFindingFilterOption {
  id: string;
  name: string;
}

export interface INewFindingFilterOptionsData {
  dimension: string; // echoes the requested dimension: ISSUE | TEST | DSN | BUILD
  options: INewFindingFilterOption[];
}

export type TNewFindingsFilterOptionsResponse = IServerResponse<INewFindingFilterOptionsData>;

// Per-dimension id filters shared by GetNewFindings and GetNewFindingsFilterOptions. Values within
// one dimension are OR'd; separate dimensions are AND'd. Serialized as repeated query params.
export interface INewFindingsFilterParams {
  issueIds?: string[];
  testCodes?: string[];
  dsnIds?: string[];
  buildIds?: string[];
}

// FA Detail (from GetFaRecordDetail)
export interface IFADetailSample {
  id: number;
  issueId: number;
  dsn: string;
  testSequenceId: number;
  label: string;
}

export interface IFADetailSampleGroup {
  testName: string;
  failureCount: number;
  failureMode: string;
  samples: IFADetailSample[];
}

export interface IFADetailHistoryEntry {
  id: number;
  summary: string;
  createdTs: string;
}

// FA Attachment item (returned by GetFaRecordDetail, sent in UpdateFaRecord)
export interface IFAAttachmentItem {
  name: string;
  s3Path: string;
  preSignedURL?: string | null;
}

export interface IFADetail {
  id: number;
  faCaseNo: string;
  status: TFAStatus;
  jiraNumber: string | null;
  summary: string | null;
  rootCause: string | null;
  correctiveActions: string | null;
  createdBy: string | null;
  createdTs: string;
  updatedTs: string;
  sampleGroups: IFADetailSampleGroup[];
  history: IFADetailHistoryEntry[];
  attachments?: IFAAttachmentItem[];
}

export type TFADetailResponse = IServerResponse<IFADetail>;

// Create FA Record request (CreateFaRecord)
export interface TCreateFARequest {
  projectId: string;
  findingIds: number[];
  status: TFAStatus; // "IN_PROGRESS" | "CLOSED"
  jiraUrl?: string;
  latestSummary?: string;
  rootCause?: string;
  correctiveActions?: string;
}

// Create FA Record response data
export interface ICreateFARecordData {
  id: number;
  faCaseNo: string;
}

export type TCreateFARecordResponse = IServerResponse<ICreateFARecordData>;

// Update FA Record request (UpdateFaRecord)
export interface TUpdateFADetailRequest {
  projectId: string;
  faCaseId: string;
  status?: TFAStatus; // "IN_PROGRESS" | "CLOSED"
  jiraUrl?: string;
  latestSummary?: string;
  rootCause?: string;
  correctiveActions?: string;
  addFindingIds?: number[];
  attachments?: IFAAttachmentItem[];
}

// Update All FA Records request (UpdateAllFaRecords)
export interface IFaSummaryUpdateEntry {
  faCaseId: string;
  summary: string;
}

export interface TUpdateAllFARecordsRequest {
  projectId: string;
  updates: IFaSummaryUpdateEntry[];
}

export interface IUpdateAllFARecordsData {
  updatedCount: number;
  skippedCount: number;
}

export type TUpdateAllFARecordsResponse = IServerResponse<IUpdateAllFARecordsData>;

// Mark Waived request (MarkFindingsWaived)
export interface TMarkWaivedRequest {
  projectId: string;
  findingIds: number[];
}

// Smart Summary (AI) request/response
export interface TSmartSummaryRequest {
  jiraUrl: string;
  faStatus?: string; // "IN_PROGRESS" | "CLOSED"
}

export interface IJiraInfo {
  issueKey: string;
  summary: string | null;
  description: string | null;
  status: string | null;
  assignee: string | null;
  reporter: string | null;
  jiraUrl: string | null;
}

export interface ISmartSummaryData {
  jiraInfo: IJiraInfo | null;
  summary: string | null;
  rootCause: string | null;
  correctiveActions: string | null;
}

export type TSmartSummaryResponse = IServerResponse<ISmartSummaryData>;

// ──────────────────────────────────────────────────────────────────────
// DataOverview module — list of devices per test
// ──────────────────────────────────────────────────────────────────────

export interface IDataOverviewProgress {
  completed: number;
  total: number;
}

/**
 * Raw status values sent by the backend.
 * - Live BE vocabulary (matches update_test_status discriminator): notStarted, ongoing, complete.
 * - Already-aligned UI vocabulary still emitted by some endpoints: toStart, queued, inProgress,
 *   pass, fail, cancelled, observation.
 * - Legacy uppercase contract retained for older fixtures.
 */
export type TDataOverviewBeStatus = "notStarted" | "ongoing" | "complete" | "toStart" | "queued" | "inProgress" | "pass" | "fail" | "cancelled" | "observation" | "NOT_STARTED" | "ONGOING" | "COMPLETED" | "CANCELLED" | "OBSERVATION";

export interface IDataOverviewDevice {
  dsn: string;
  realDsn: string;
  deviceDisplayName: string;
  configName: string;
  sequenceId: string;
  status: TDataOverviewBeStatus;
  hasCmApproved: boolean;
  completedDate: string;
  hasAllMajorReadoutDates: boolean;
  isOverdue: boolean;
  overallProgress: IDataOverviewProgress;
  checkProgress: IDataOverviewProgress;
  fatpProgress: IDataOverviewProgress;
  measurementProgress: IDataOverviewProgress;
  variableProgress: IDataOverviewProgress;
}

export type TDataOverviewListResponse = IServerResponse<IDataOverviewDevice[]>;

// =====================================================================
// DataOverview — Phase 2 (batch actions)
// =====================================================================

// Readout list (per sequence)
export interface IReadoutListItem {
  id: number;
  order: number;
  keyValuePair: IReadoutKeyValuePair[];
  isMajorCheckpoint: boolean;
  milestoneLabel?: string;
}
export type TReadoutListResponse = IServerResponse<IReadoutListItem[]>;

// BE FatpReadoutItem — a single readout with its grouped key-value pairs. Shared by both
// FATP readout endpoints: GET /get_fatp_readouts (the DataOverview drawer, flat list) and
// GET /get_readouts_with_fatp_check (the FATPViewer tree, nested under each test/sequence).
// The BE already groups each readout's pairs into `keyValuePairs`. Note: the wire key is
// `keyValuePairs` (plural), whereas the FE's IReadoutListItem uses `keyValuePair`.
export interface IFatpReadoutOption {
  id: number;
  order: number;
  keyValuePairs: IReadoutKeyValuePair[];
}
export type TFatpReadoutsResponse = IServerResponse<IFatpReadoutOption[]>;

// Test variable selections (test-wide; no sequenceId)
export interface ITestVariableSelection {
  key: string;
  name: string;
  values: string[];
}
export type TTestVariableSelectionsResponse = IServerResponse<ITestVariableSelection[]>;

// Major readout list (Update Schedule drawer) — GET /get_major_readout_list.
// BE returns only the major readouts plus the final (last) readout for a test/sequence,
// each carrying its currently-scheduled `date` (if any). This single endpoint supplies
// both the drawer's checkpoint rows and their date prefill (replacing the old split of
// get_readout_list + get_device_checkpoint_dates).
export interface IMajorReadoutItem {
  id: number;
  order: number;
  isMajorReadout: boolean;
  date?: string; // ISO yyyy-mm-dd; absent when no date has been scheduled yet
  keyValuePair: IReadoutKeyValuePair[];
}
export type TMajorReadoutListResponse = IServerResponse<IMajorReadoutItem[]>;

// Batch action request bodies
interface IBatchRequestBase extends TIdentifierObject {
  dsns: string[];
}
export type TBatchMarkOngoingRequest = IBatchRequestBase;
export type TBatchMarkCompletedRequest = IBatchRequestBase;
export type TBatchResetRequest = IBatchRequestBase;
export type TBatchApproveRequest = IBatchRequestBase;
export interface TBatchPassReadoutsRequest extends IBatchRequestBase {
  sequenceId: string;
  readoutIds: number[];
}
// BE BatchSetVariablesInput reads a per-device `tests: [{ sequenceId, dsn }]` list (same
// shape as TUpdateTestStatusRequest), NOT the flat `dsns` array — sending `dsns` left the
// BE's getTests() null → NullPointerException (Asana 1216844801835600).
export interface TBatchSetVariablesRequest extends TIdentifierObject {
  tests: { sequenceId: string; dsn: string }[];
  variables: { key: string; value: string }[];
}
// POST /change_readout_schedule (BE ChangeReadoutScheduleInput). Inserts or updates
// schedule dates for the given devices. checkpoint `id` is the readoutId; `date` must be
// ISO yyyy-mm-dd (BE input model enforces the pattern \d{4}-\d{2}-\d{2}) — sent as-is from
// the UI date picker, no reformatting at the service layer.
export interface TChangeReadoutScheduleRequest extends IBatchRequestBase {
  sequenceId: string;
  checkpoints: { id: number; date: string }[];
}
export interface TBatchLinkFATPLogsRequest extends IBatchRequestBase {
  sequenceId: string;
  stationId: string;
  readoutId: number;
  fromDate: string;
  toDate: string;
  assignments: { dsn: string; logId: string; issueId?: number }[];
}
export type TBatchActionResponse = IServerResponse<unknown>;

// Batch approve (Approval Center). The envelope is isSuccess:true even when every item fails, so
// callers must inspect succeeded/failed + per-item results rather than the envelope alone.
export interface IBatchApproveItemResult {
  approvalId: number;
  isSuccess: boolean;
  message?: string;
}
export interface IBatchApproveData {
  succeeded: number;
  failed: number;
  results: IBatchApproveItemResult[];
}
export type TBatchApproveResponse = IServerResponse<IBatchApproveData>;

// Unified test-status update (replaces markOngoing / markCompleted / reset / approve)
export type TUpdateTestStatusValue = "ongoing" | "complete" | "toStart" | "approve";
export interface TUpdateTestStatusRequest extends TIdentifierObject {
  status: TUpdateTestStatusValue;
  tests: { sequenceId: string; dsn: string }[];
}

// ── Issue List ────────────────────────────────────────────────────────

export type TIssueRecovery = "Non-Recoverable" | "Requires User Intervention" | "Self-Recoverable";
export type TIssueSeverity = "Failure" | "Observation";
export type TIssueCategory = "Cosmetic" | "Functional" | "FATP" | "Mechanical";
export type TIssueTranslation = Partial<Record<"zh" | "vi" | "es" | "th", string>>;

export interface IIssueListItem {
  issueId: number;
  failureMode: string;
  failingComponent?: string;
  location?: string;
  details?: string;
  recovery?: TIssueRecovery;
  recoverySteps?: string;
  severity: TIssueSeverity;
  category: TIssueCategory;
  translation: TIssueTranslation;
  isMappedWithChecks?: boolean;
}

export type TIssueListResponse = IServerResponse<IIssueListItem[]>;

// =====================================================================
// FATPViewer
// =====================================================================

export type TParameterClass = "sequence" | "independent" | "nonNumeric";

// ── FATPViewer wire types (BE-aligned) ───────────────────────────────
export type TFATPParameterType = "SEQUENCE PARAMETERS" | "INDEPENDENT NUMERIC PARAMETERS" | "NON-NUMERIC";

// get_fatp_configs
export interface IFATPConfigItem {
  config: string;
  dsn: string;
  shortId: string;
  order: number;
}
// get_fatp_configs — intentionally NOT extending TIdentifierParams: the BE
// GetFatpConfigs operation takes only checkCode + testReadoutIds (no identifier scope).
export interface TFATPConfigsRequest {
  checkCode: string;
  testReadoutIds: number[];
}
export type TFATPConfigsResponse = IServerResponse<IFATPConfigItem[]>;

// get_fatp_parameters
export interface IFATPParameterItem {
  parameter: string;
  count: number;
  type: TFATPParameterType;
}
export type TFATPParametersRequest = TIdentifierParams & {
  checkCode: string;
  dsns: string[];
  testReadoutIds: number[];
};
export type TFATPParametersResponse = IServerResponse<IFATPParameterItem[]>;

// get_fatp_parameter_data
export interface IFATPDataSeries {
  dsn?: string;
  shortId?: string;
  config?: string;
  readoutKey?: string;
  readoutValue?: string;
  testReadoutId?: number;
  type: string; // "DEFAULT" | "UPPER_LIMIT" | "LOWER_LIMIT" | "BASELINE"
  x: number[];
  y: number[];
}
export interface IFATPParameterDataGroup {
  parameter: string;
  points: IFATPDataSeries[];
}
export type TFATPParameterDataRequest = TIdentifierParams & {
  checkCode: string;
  dsns: string[];
  testReadoutIds: number[];
  parameters: { name: string; type: TFATPParameterType }[];
};
export type TFATPParameterDataResponse = IServerResponse<IFATPParameterDataGroup[]>;

// ── Raw parameter export (start_fatp_raw_export / get_fatp_raw_export_status) ─────────
// Deliberately NOT a variant of TFATPParameterDataRequest: this export takes no `parameters`
// list — exporting every parameter is the whole point of the operation. The job is asynchronous
// because a realistic selection is hundreds of thousands of rows and takes longer than the 60s
// ALB idle timeout to build, so the CSV is delivered by presigned S3 URL, not on the wire.
export type TFATPRawExportState = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED";

export type TFATPRawExportRequest = TIdentifierParams & {
  checkCode: string;
  dsns: string[];
  testReadoutIds: number[];
};

export interface IFATPRawExportSubmission {
  jobId: string;
  state: TFATPRawExportState;
  /** Queue depth at submission; 0 when the job started immediately. */
  queuePosition?: number;
}
export type TFATPRawExportSubmissionResponse = IServerResponse<IFATPRawExportSubmission>;

// Every field after `state` is conditionally present — `downloadUrl` only on SUCCEEDED,
// `message` only on FAILED, the counts only once the file has been built. Treating any of them
// as required would compile and then read `undefined` at runtime.
export interface IFATPRawExportStatus {
  jobId: string;
  state: TFATPRawExportState;
  /** Human-readable progress line, e.g. "Fetched 50000 rows". Shown verbatim. */
  progress: string;
  /** Presigned S3 URL. Present only when SUCCEEDED; expires one hour after the job finishes. */
  downloadUrl?: string;
  /** Data rows written, excluding the header. */
  rowCount?: number;
  /** Logs the database expected but Athena had no rows for. Informational, not an error. */
  gapLogCount?: number;
  /** User-facing failure reason. Present only when FAILED. */
  message?: string;
}
export type TFATPRawExportStatusResponse = IServerResponse<IFATPRawExportStatus>;

// Test → readout tree (FATPViewer)
//
// Returned by fetch_FATPViewer_ReadoutTree (GET /get_readouts_with_fatp_check). The BE
// (FatpReadoutSequenceItem) already nests the data as test/sequence → readouts, so the FE
// consumes it directly (no client-side grouping). Each readout is a BE FatpReadoutItem
// (IFatpReadoutOption): only `id`, `order`, and `keyValuePairs` are present — there is no
// `isFinal`/`measurements`/`checks` on this endpoint.
export interface IFATPViewerReadoutTreeTest {
  testId: string;
  testName: string;
  sequenceId: string;
  readouts: IFatpReadoutOption[];
}

export interface IFATPViewerReadoutTree {
  tests: IFATPViewerReadoutTreeTest[];
}

// Wire shape of GET /get_readouts_with_fatp_check: the BE returns the nested tree directly
// (data: IFATPViewerReadoutTreeTest[]). fetch_FATPViewer_ReadoutTree wraps it into
// { tests } for the panels.
export type TFATPViewerReadoutTreeResponse = IServerResponse<IFATPViewerReadoutTreeTest[]>;

// Legacy shim type kept for one release while V1 callers migrate.
// Remove once all FATPViewer code reads IFATPViewerReadoutTree.
export interface ITestReadoutTree {
  tests: {
    id: string;
    name: string;
    readouts: { id: string; name: string }[];
  }[];
}

// failureMode + category + severity are @required only on the FE registry form, NOT on the
// backend AddIssueRecordInput (there only projectId is @required). They are optional here so
// the ReadoutInput "quick" operator path can create a translation-only placeholder issue.
export type TAddIssueRequest = { projectId: string; familyId: string; testLocationId: string; userName?: string } & Omit<IIssueListItem, "issueId" | "isMappedWithChecks" | "failureMode" | "category" | "severity"> &
  Partial<Pick<IIssueListItem, "failureMode" | "category" | "severity">>;
export type TEditIssueRequest = { projectId: string; familyId: string; testLocationId: string; userName?: string } & IIssueListItem;
export interface TDeleteIssueRequest {
  projectId: string;
  issueId: number;
}

export interface TCopyIssueRequest {
  sourceProjectId: string;
  destProjectId: string;
  issueIdList?: number[];
  userName?: string;
}

export interface TMergeIssueRequest {
  projectId: string;
  issueIdToKeep: number;
  issueIdToMergeList: number[];
  username?: string;
}

export interface IIssueForCopy {
  issueId: number;
  description?: string;
  failureMode?: string;
  failingComponent?: string;
  location?: string;
  recovery?: TIssueRecovery;
  recoverySteps?: string;
}

export type TIssueListForCopyResponse = IServerResponse<IIssueForCopy[]>;

// =====================================================================
// UV Visualizer
// =====================================================================

// GetUvColorTable (BE: GET /api/get_color_table) returns the standard
// BaseResponse envelope with data: UvComponentItemList.
export type TUVVisualizerColorTableResponse = IServerResponse<TUvColorTableResponse>;

// BatchGenerateSmartSummary (powers "Update All - Smart Summary" dialog)
export interface IBatchSmartSummaryItem {
  faCaseId: string;
  jiraUrl: string;
  faStatus: TFAStatus;
}

export interface IBatchSmartSummaryResultItem {
  faCaseId: string;
  summary: string | null;
  rootCause: string | null;
  correctiveActions: string | null;
  error: string | null;
}

export interface TBatchSmartSummaryRequest {
  items: IBatchSmartSummaryItem[];
}

export interface IBatchSmartSummaryData {
  results: IBatchSmartSummaryResultItem[];
}

export type TBatchSmartSummaryResponse = IServerResponse<IBatchSmartSummaryData>;

// UnlinkFinding
export interface TUnlinkFindingRequest {
  projectId: string;
  faCaseId: string;
  findingId: number;
}
// ── ESD Calculator ────────────────────────────────────────────────────────

// FE-side types (used in UI state)
export type TESDDeviceType = "highTouch" | "lowTouch";
export type TESDLocationCategory = "Buttons" | "Openings" | "Screen" | "Surfaces";

export interface IESDLocationCategoryItem {
  location: string;
  category: TESDLocationCategory;
}

export interface IESDTestResultRow {
  location: string;
  voltageResults: IESDVoltageResult[];
}

export interface IESDVoltageResult {
  voltage: number;
  zaps: number;
  t1: number | null;
  t2: number | null;
  t3: number | null;
}

// ── Backend API types (matching Smithy model) ─────────────────────────────

/** POST /api/esd/predict_reliability — request payload */
export interface IESDPredictRequest {
  deviceType: number; // 0-10 integer index
  voltages: number[]; // sorted ascending, mixed positive/negative
  zapPointNames: string[]; // parallel array: location names
  zapPointCategories: number[]; // parallel array: 1-4 integers
  zaps: number[][]; // [location][voltage] matrix
  t1: number[][]; // [location][voltage] matrix
  t2: number[][]; // [location][voltage] matrix
  t3: number[][]; // [location][voltage] matrix
}

/** POST /api/esd/predict_reliability — response data */
export interface IESDReliabilityData {
  version: string;
  t1: number; // reliability % for Type 1 failures
  t2: number; // reliability % for Type 2 failures
  t3: number; // reliability % for Type 3 failures
  t23: number; // reliability % for Type 2+3 combined
  t123: number; // reliability % for Type 1+2+3 combined
}

export type TESDPredictResponse = IServerResponse<IESDReliabilityData>;

/** POST /api/esd/generate_test_plan — request payload */
export interface IESDGenerateTestPlanRequest {
  deviceType: number;
  voltages: number[];
  zapPointNames: string[];
  zapPointCategories: number[];
  targetZapCount: number;
  numUnits: number;
}

/** POST /api/esd/generate_test_plan — response data */
export interface IESDTestPlanData {
  version: string;
  voltages: number[]; // ascending; column order of the matrices below
  zapPointNames: string[];
  zapPointCategories: number[];
  /**
   * [location][voltage] zap counts for a SINGLE unit — the grid Classic RADAR+ displays
   * (its NumZapsPerLocation). Optional because backends predating this field omit it;
   * derive it from zapResults / numUnits when absent.
   */
  numZapsPerLocation?: number[][];
  zapResults: number[][]; // [location][voltage] — already multiplied by numUnits
  predictedConfidence: number; // predicted first-year reliability %
}

export type TESDGenerateTestPlanResponse = IServerResponse<IESDTestPlanData>;

/** GET /api/esd/get_project_data — request params */
export interface IESDProjectDataRequest {
  familyId?: string;
  projectId?: string;
  testLocationId?: string;
  phaseId?: string;
  buildId?: string;
  testCodes?: string[];
}

/** GET /api/esd/get_project_data — response data */
export interface IESDProjectData {
  voltages: number[];
  zapPointNames: string[];
  zaps: number[][];
  t1: number[][];
  t2: number[][];
  t3: number[][];
}

export type TESDProjectDataResponse = IServerResponse<IESDProjectData>;

/** POST /api/esd/import_test_plan — request payload */
export interface IESDImportTestPlanRequest {
  familyId: string;
  projectId: string;
  testLocationId: string;
  phaseId: string;
  buildId: string;
  testCode: string;
  rows: { voltage: number; location: string; zaps: number; majorReadout?: boolean }[];
}

export interface IESDImportTestPlanData {
  insertedCount: number;
  updatedCount: number;
}

export type TESDImportTestPlanResponse = IServerResponse<IESDImportTestPlanData>;

/** One zap-point's location-category assignment (backend: EsdLocationCategoryMapping) */
export interface IESDLocationCategoryMapping {
  zapPointName: string;
  /** 1=Buttons/Jacks/Connectors, 2=Case Openings, 3=Touch Screen, 4=Misc Surfaces */
  category: number;
}

/** Per (project, test location) ESD settings: device type + zap-point category mappings */
export interface IESDSettings {
  deviceType: number | null;
  locationCategories: IESDLocationCategoryMapping[];
}

/** POST /api/esd/save_settings — request payload */
export interface IESDSaveSettingsRequest {
  projectId: string;
  testLocationId: string;
  deviceType: number;
  locationCategories: IESDLocationCategoryMapping[];
}

export type TESDSaveSettingsResponse = IServerResponse<IESDSettings>;

/** GET /api/esd/get_settings — query params */
export interface IESDGetSettingsRequest {
  projectId: string;
  testLocationId: string;
}

export type TESDGetSettingsResponse = IServerResponse<IESDSettings>;

// ── Approval Center ─────────────────────────────────────────────────────────

export type TApprovalSource = "build" | "family" | "signoff";
export type TApprovalStatus = "PENDING" | "APPROVED" | "REJECTED";

/** Common approval request (table row) */
export interface IApprovalRequest {
  id: number;
  title: string;
  source: TApprovalSource;
  sourceLabel: string;
  category: string;
  project: string;
  build: string;
  status: TApprovalStatus;
  requester: string;
  requesterInitial: string;
  submittedDate: string;
  daysAgo: number;
  approverNames?: string[];
  approvalPath?: "committee" | "standard";
  isSignOff: boolean;
}

export type TApprovalListResponse = IServerResponse<IApprovalRequest[]>;

/** Approver entry */
export interface IApprover {
  name: string;
  status: TApprovalStatus;
  isCommittee?: boolean;
  delegate?: string;
  members?: string[];
}

/** Timeline entry */
export interface IApprovalTimelineEntry {
  action: string;
  by: string;
  date: string;
  type: "submitted" | "approved" | "rejected";
  comment?: string;
}

/** Full spec approval detail (for drawer) */
export interface IApprovalDetail {
  id: number;
  title: string;
  source: TApprovalSource;
  sourceLabel: string;
  category: string;
  project: string;
  build: string;
  status: TApprovalStatus;
  requester: string;
  requesterInitial: string;
  submittedDate: string;
  daysAgo: number;
  approvalPath?: "committee" | "standard";
  description: string;
  passCriteria: string;
  changes: string;
  justificationFiles: string[];
  approvers: IApprover[];
  timeline: IApprovalTimelineEntry[];
}

export type TApprovalDetailResponse = IServerResponse<IApprovalDetail>;

/** Sign-off detail (for drawer) */
export interface ISignOffDetail {
  id: number;
  project: string;
  build: string;
  status: TApprovalStatus;
  signOffType: string;
  requester: string;
  requesterInitial: string;
  submittedDate: string;
  daysAgo: number;
  description: string;
  justificationFiles: string[];
  testList: string[];
  approvers: IApprover[];
  timeline: IApprovalTimelineEntry[];
}

export type TSignOffDetailResponse = IServerResponse<ISignOffDetail>;

/** Approve request payload */
export interface IApprovalApproveRequest {
  approvalId: number;
  comment?: string;
}

/** Reject request payload (comment required) */
export interface IApprovalRejectRequest {
  approvalId: number;
  comment: string;
}

/** Recall request payload */
export interface IApprovalRecallRequest {
  approvalId: number;
}

/** Batch approve payload */
export interface IApprovalBatchApproveRequest {
  approvalIds: number[];
  comment?: string;
}

/** Add approver payload */
export interface IApprovalAddApproverRequest {
  approvalId: number;
  approverAlias: string;
}

/** Transfer approval payload */
export interface IApprovalTransferRequest {
  approvalId: number;
  toAlias: string;
}
